/*
 * Modified version of https://github.com/palantir/blueprint/blob/develop/packages/datetime/src/components/time-picker/timePicker.tsx
 * - maintain our own version since Blueprint TimezoneSelect is deprecated and does not support React-18
 */

import {
  Classes as CoreClasses,
  Utils as CoreUtils,
  DISPLAYNAME_PREFIX,
  HTMLSelect,
  Icon,
  Intent,
} from "@blueprintjs/core";
import classNames from "classnames";
import React from "react";

import {
  clone,
  convert24HourMeridiem,
  get12HourFrom24Hour,
  getIsPmFrom24Hour,
  getTimeInRange,
  isSameTime,
  isTimeInRange,
} from "./dateUtils";
import { Classes } from "./timepickerClasses";
import { type TimePickerProps } from "./timePickerProps";
import { TimePrecision } from "./timePrecision";
import {
  TimeUnit,
  getDefaultMaxTime,
  getDefaultMinTime,
  getTimeUnit,
  getTimeUnitClassName,
  getTimeUnitMax,
  getTimeUnitPrintStr,
  isTimeUnitValid,
  setTimeUnit,
  wrapTimeAtUnit,
} from "./timeUnit";

function stringRepeat(str: string, numTimes: number) {
  return new Array(numTimes + 1).join(str);
}

function padWithZeroes(str: string, minLength: number) {
  if (str.length < minLength) {
    return `${stringRepeat("0", minLength - str.length)}${str}`;
  } else {
    return str;
  }
}

export interface TimePickerState {
  hourText: string;
  minuteText: string;
  secondText: string;
  millisecondText: string;
  value: Date;
  isPm: boolean;
}

/**
 * Time picker component.
 *
 * @see https://blueprintjs.com/docs/#datetime/timepicker
 */
export class TimePicker extends React.Component<
  TimePickerProps,
  TimePickerState
> {
  public static defaultProps: TimePickerProps = {
    autoFocus: false,
    disabled: false,
    maxTime: getDefaultMaxTime(),
    minTime: getDefaultMinTime(),
    precision: TimePrecision.MINUTE,
    selectAllOnFocus: false,
    showArrowButtons: false,
    useAmPm: false,
  };

  public static displayName = `${DISPLAYNAME_PREFIX}.TimePicker`;

  public constructor(props: TimePickerProps) {
    super(props);
    this.state = this.getFullStateFromValue(
      this.getInitialValue(),
      this.props.useAmPm,
    );
  }

  private timeInputIds: { [key in TimeUnit]: string } = {
    [TimeUnit.HOUR_24]: CoreUtils.uniqueId(TimeUnit.HOUR_24 + "-input"),
    [TimeUnit.HOUR_12]: CoreUtils.uniqueId(TimeUnit.HOUR_12 + "-input"),
    [TimeUnit.MINUTE]: CoreUtils.uniqueId(TimeUnit.MINUTE + "-input"),
    [TimeUnit.SECOND]: CoreUtils.uniqueId(TimeUnit.SECOND + "-input"),
    [TimeUnit.MS]: CoreUtils.uniqueId(TimeUnit.MS + "-input"),
  };

  public override render() {
    const shouldRenderMilliseconds =
      this.props.precision === TimePrecision.MILLISECOND;
    const shouldRenderSeconds =
      shouldRenderMilliseconds || this.props.precision === TimePrecision.SECOND;
    const shouldRenderMinutes =
      shouldRenderSeconds || this.props.precision === TimePrecision.MINUTE;
    const hourUnit = this.props.useAmPm ? TimeUnit.HOUR_12 : TimeUnit.HOUR_24;
    const classes = classNames(Classes.TIMEPICKER, this.props.className, {
      [CoreClasses.DISABLED]: this.props.disabled,
    });

    return (
      <div className={classes}>
        <div className={Classes.TIMEPICKER_ARROW_ROW}>
          {this.maybeRenderArrowButton(true, hourUnit)}
          {shouldRenderMinutes &&
            this.maybeRenderArrowButton(true, TimeUnit.MINUTE)}
          {shouldRenderSeconds &&
            this.maybeRenderArrowButton(true, TimeUnit.SECOND)}
          {shouldRenderMilliseconds &&
            this.maybeRenderArrowButton(true, TimeUnit.MS)}
        </div>
        <div className={Classes.TIMEPICKER_INPUT_ROW}>
          {this.renderInput(
            Classes.TIMEPICKER_HOUR,
            hourUnit,
            this.state.hourText,
          )}
          {shouldRenderMinutes && this.renderDivider()}
          {shouldRenderMinutes &&
            this.renderInput(
              Classes.TIMEPICKER_MINUTE,
              TimeUnit.MINUTE,
              this.state.minuteText,
            )}
          {shouldRenderSeconds && this.renderDivider()}
          {shouldRenderSeconds &&
            this.renderInput(
              Classes.TIMEPICKER_SECOND,
              TimeUnit.SECOND,
              this.state.secondText,
            )}
          {shouldRenderMilliseconds && this.renderDivider(".")}
          {shouldRenderMilliseconds &&
            this.renderInput(
              Classes.TIMEPICKER_MILLISECOND,
              TimeUnit.MS,
              this.state.millisecondText,
            )}
        </div>
        {this.maybeRenderAmPm()}
        <div className={Classes.TIMEPICKER_ARROW_ROW}>
          {this.maybeRenderArrowButton(false, hourUnit)}
          {shouldRenderMinutes &&
            this.maybeRenderArrowButton(false, TimeUnit.MINUTE)}
          {shouldRenderSeconds &&
            this.maybeRenderArrowButton(false, TimeUnit.SECOND)}
          {shouldRenderMilliseconds &&
            this.maybeRenderArrowButton(false, TimeUnit.MS)}
        </div>
      </div>
    );
  }

  public override componentDidUpdate(prevProps: TimePickerProps) {
    const didMinTimeChange = prevProps.minTime !== this.props.minTime;
    const didMaxTimeChange = prevProps.maxTime !== this.props.maxTime;
    const didBoundsChange = didMinTimeChange || didMaxTimeChange;
    const didPropValueChange = prevProps.value !== this.props.value;
    const shouldStateUpdate = didBoundsChange || didPropValueChange;

    let value = this.state.value;
    if (!this.props.value) {
      value = this.getInitialValue();
    }
    if (didBoundsChange) {
      value = getTimeInRange(
        this.state.value ?? this.getInitialValue(),
        this.props.minTime ?? getDefaultMinTime(),
        this.props.maxTime ?? getDefaultMaxTime(),
      );
    }
    if (
      this.props.value &&
      !isSameTime(this.props.value, prevProps.value ?? null)
    ) {
      value = this.props.value;
    }

    if (shouldStateUpdate) {
      this.setState(this.getFullStateFromValue(value, this.props.useAmPm));
    }
  }

  // begin method definitions: rendering

  private maybeRenderArrowButton(isDirectionUp: boolean, timeUnit: TimeUnit) {
    if (!this.props.showArrowButtons) {
      return null;
    }
    const classes = classNames(
      Classes.TIMEPICKER_ARROW_BUTTON,
      getTimeUnitClassName(timeUnit),
    );
    const onClick = () =>
      (isDirectionUp ? this.incrementTime : this.decrementTime)(timeUnit);
    const label = `${isDirectionUp ? "Increase" : "Decrease"} ${getTimeUnitPrintStr(timeUnit)}`;

    // set tabIndex=-1 to ensure a valid FocusEvent relatedTarget when focused
    return (
      <span
        aria-controls={this.timeInputIds[timeUnit]}
        aria-label={label}
        className={classes}
        tabIndex={-1}
        onClick={onClick}
      >
        <Icon
          icon={isDirectionUp ? "chevron-up" : "chevron-down"}
          title={label}
        />
      </span>
    );
  }

  private renderDivider(text = ":") {
    return <span className={Classes.TIMEPICKER_DIVIDER_TEXT}>{text}</span>;
  }

  private renderInput(className: string, unit: TimeUnit, value: string) {
    const valueNumber = parseInt(value, 10);
    const isValid = isTimeUnitValid(unit, valueNumber);
    const isHour = unit === TimeUnit.HOUR_12 || unit === TimeUnit.HOUR_24;

    return (
      <input
        aria-label={getTimeUnitPrintStr(unit)}
        autoFocus={isHour && this.props.autoFocus}
        className={classNames(
          Classes.TIMEPICKER_INPUT,
          { [CoreClasses.intentClass(Intent.DANGER)]: !isValid },
          className,
        )}
        disabled={this.props.disabled}
        id={this.timeInputIds[unit]}
        max={getTimeUnitMax(unit)}
        min={0}
        role={this.props.showArrowButtons ? "spinbutton" : undefined}
        type="number"
        value={value}
        onBlur={this.getInputBlurHandler(unit)}
        onChange={this.getInputChangeHandler(unit)}
        onFocus={this.getInputFocusHandler(unit)}
        onKeyDown={this.getInputKeyDownHandler(unit)}
        onKeyUp={this.getInputKeyUpHandler(unit)}
      />
    );
  }

  private maybeRenderAmPm() {
    if (!this.props.useAmPm) {
      return null;
    }
    return (
      <HTMLSelect
        className={Classes.TIMEPICKER_AMPM_SELECT}
        disabled={this.props.disabled}
        value={this.state.isPm ? "pm" : "am"}
        onChange={this.handleAmPmChange}
      >
        <option value="am">AM</option>
        <option value="pm">PM</option>
      </HTMLSelect>
    );
  }

  // begin method definitions: event handlers

  private getInputChangeHandler =
    (unit: TimeUnit) => (e: React.SyntheticEvent<HTMLInputElement>) => {
      const text = getStringValueFromInputEvent(e);
      switch (unit) {
        case TimeUnit.HOUR_12:
        case TimeUnit.HOUR_24:
          this.setState({ hourText: text });
          break;
        case TimeUnit.MINUTE:
          this.setState({ minuteText: text });
          break;
        case TimeUnit.SECOND:
          this.setState({ secondText: text });
          break;
        case TimeUnit.MS:
          this.setState({ millisecondText: text });
          break;
      }
    };

  private getInputBlurHandler =
    (unit: TimeUnit) => (e: React.FocusEvent<HTMLInputElement>) => {
      const text = getStringValueFromInputEvent(e);
      this.updateTime(parseInt(text, 10), unit);
      this.props.onBlur?.(e, unit);
    };

  private getInputFocusHandler =
    (unit: TimeUnit) => (e: React.FocusEvent<HTMLInputElement>) => {
      if (this.props.selectAllOnFocus) {
        e.currentTarget.select();
      }
      this.props.onFocus?.(e, unit);
    };

  private getInputKeyDownHandler =
    (unit: TimeUnit) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      handleKeyEvent(e, {
        ArrowDown: () => this.decrementTime(unit),
        ArrowUp: () => this.incrementTime(unit),
        Enter: () => {
          (e.currentTarget as HTMLInputElement).blur();
        },
      });
      this.props.onKeyDown?.(e, unit);
    };

  private getInputKeyUpHandler =
    (unit: TimeUnit) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      this.props.onKeyUp?.(e, unit);
    };

  private handleAmPmChange = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const isNextPm = e.currentTarget.value === "pm";
    if (isNextPm !== this.state.isPm) {
      const hour = convert24HourMeridiem(this.state.value.getHours(), isNextPm);
      this.setState({ isPm: isNextPm }, () =>
        this.updateTime(hour, TimeUnit.HOUR_24),
      );
    }
  };

  // begin method definitions: state modification

  /**
   * Generates a full TimePickerState object with all text fields set to formatted strings based on value
   */
  private getFullStateFromValue(
    value: Date | undefined,
    useAmPm: boolean = false,
  ): TimePickerState {
    value = value ?? this.getInitialValue();
    const timeInRange = getTimeInRange(
      value,
      this.props.minTime ?? getDefaultMinTime(),
      this.props.maxTime ?? getDefaultMaxTime(),
    );
    const hourUnit = useAmPm ? TimeUnit.HOUR_12 : TimeUnit.HOUR_24;
    /* tslint:disable:object-literal-sort-keys */
    return {
      hourText: formatTime(timeInRange.getHours(), hourUnit),
      minuteText: formatTime(timeInRange.getMinutes(), TimeUnit.MINUTE),
      secondText: formatTime(timeInRange.getSeconds(), TimeUnit.SECOND),
      millisecondText: formatTime(timeInRange.getMilliseconds(), TimeUnit.MS),
      value: timeInRange,
      isPm: getIsPmFrom24Hour(timeInRange.getHours()),
    };
    /* tslint:enable:object-literal-sort-keys */
  }

  private incrementTime = (unit: TimeUnit) => this.shiftTime(unit, 1);

  private decrementTime = (unit: TimeUnit) => this.shiftTime(unit, -1);

  private shiftTime(unit: TimeUnit, amount: number) {
    if (this.props.disabled) {
      return;
    }
    const newTime = getTimeUnit(unit, this.state.value) + amount;
    this.updateTime(wrapTimeAtUnit(unit, newTime), unit);
  }

  private updateTime(time: number, unit: TimeUnit) {
    const value = this.state.value ?? this.getInitialValue();
    const newValue = clone(value);

    if (isTimeUnitValid(unit, time)) {
      setTimeUnit(unit, time, newValue, this.state.isPm);
      if (
        isTimeInRange(
          newValue,
          this.props.minTime ?? getDefaultMinTime(),
          this.props.maxTime ?? getDefaultMaxTime(),
        )
      ) {
        this.updateState({ value: newValue });
      } else {
        this.updateState(
          this.getFullStateFromValue(this.state.value, this.props.useAmPm),
        );
      }
    } else {
      this.updateState(
        this.getFullStateFromValue(this.state.value, this.props.useAmPm),
      );
    }
  }

  private updateState(state: TimePickerState | Pick<TimePickerState, "value">) {
    let newState = state;
    const hasNewValue =
      newState.value && !isSameTime(newState.value, this.state.value);

    if (!this.props.value) {
      // component is uncontrolled
      if (hasNewValue) {
        newState = this.getFullStateFromValue(
          newState.value,
          this.props.useAmPm,
        );
      }
      this.setState(newState);
    } else {
      // component is controlled, and there's a new value
      // so set inputs' text based off of _old_ value and later fire onChange with new value
      if (hasNewValue) {
        this.setState(
          this.getFullStateFromValue(this.state.value, this.props.useAmPm),
        );
      } else {
        // no new value, this means only text has changed (from user typing)
        // we want inputs to change, so update state with new text for the inputs
        // but don't change actual value
        this.setState({
          ...newState,
          value: clone(this.state.value),
        });
      }
    }

    if (hasNewValue) {
      this.props.onChange?.(newState.value);
    }
  }

  private getInitialValue(): Date {
    const minTime = this.props.minTime ?? getDefaultMinTime();
    let value = minTime;
    if (this.props.value) {
      value = this.props.value;
    } else if (this.props.defaultValue) {
      value = this.props.defaultValue;
    }

    return value;
  }
}

function formatTime(time: number, unit: TimeUnit) {
  switch (unit) {
    case TimeUnit.HOUR_24:
      return time.toString();
    case TimeUnit.HOUR_12:
      return get12HourFrom24Hour(time).toString();
    case TimeUnit.MINUTE:
    case TimeUnit.SECOND:
      return padWithZeroes(time.toString(), 2);
    case TimeUnit.MS:
      return padWithZeroes(time.toString(), 3);
    default:
      throw Error("Invalid TimeUnit");
  }
}

function getStringValueFromInputEvent(
  e: React.SyntheticEvent<HTMLInputElement>,
) {
  return (e.target as HTMLInputElement).value;
}

interface KeyEventMap {
  [key: string]: () => void;
}

function handleKeyEvent(
  e: React.KeyboardEvent<HTMLInputElement>,
  actions: KeyEventMap,
  preventDefault = true,
) {
  for (const key of Object.keys(actions)) {
    if (e.key === key) {
      if (preventDefault) {
        e.preventDefault();
      }
      actions[key]();
    }
  }
}
